import { useEffect, useRef, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { RedeployApplicationDialog } from './RedeployApplicationDialog';
import { colors } from '../theme';
import { redeployApplication, getWorkflowStatus } from '../api';
import { useIdentity } from '../useIdentity';
import type { ApplicationSummary } from '../types';

/** "Edit environment" on the Application Details page: opens the same dialog
 * the table's Redeploy action uses, and polls the redeploy to completion. */
export function EditEnvironmentButton({ app, onDone }: { app: ApplicationSummary; onDone?: () => void }) {
  const identityApi = useApi(identityApiRef);
  const { email } = useIdentity();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  const confirm = async (payload: {
    envVars?: Record<string, string>;
    removeEnvKeys?: string[];
    rebuildFromRepo: boolean;
  }) => {
    setBusy(true);
    setError(null);
    setStage(null);
    try {
      const { workflowId } = await redeployApplication(identityApi, email, app.id, app.name, payload);
      for (let attempt = 0; attempt < 120; attempt++) {
        if (cancelled.current) return;
        const res = await getWorkflowStatus(identityApi, workflowId);
        if (res.result) {
          const finalStatus = String((res.result as { status?: unknown }).status ?? '');
          if (finalStatus === 'REDEPLOYED') {
            setBusy(false);
            setOpen(false);
            onDone?.();
            return;
          }
          throw new Error(String((res.result as { failureMessage?: unknown }).failureMessage ?? 'redeploy failed'));
        }
        setStage(res.status);
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }
      throw new Error('redeploy is taking longer than expected — check the Temporal workflow');
    } catch (err) {
      if (cancelled.current) return;
      setBusy(false);
      setStage(null);
      setError((err as Error).message);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          border: `1px solid ${colors.border}`,
          background: colors.surface,
          color: colors.primary,
          borderRadius: 8,
          padding: '8px 14px',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Edit environment
      </button>
      {open && (
        <RedeployApplicationDialog
          app={app}
          busy={busy}
          stage={stage}
          error={error}
          onCancel={() => setOpen(false)}
          onConfirm={confirm}
        />
      )}
    </>
  );
}
